import type { ReactNode } from 'react'
import type { CartLine, MenuItem } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { toCents } from '@/lib/format'

import { ORDER_RULES } from '@/constants/site'

const STORAGE_KEY = 'la-douceur:order'

export type CartItem = Pick<MenuItem, 'slug' | 'name' | 'price' | 'image'>

export type CartNotice = {
    id: number
    name: string
    quantity: number
}

type CartContextValue = {
    lines: Array<CartLine>
    count: number
    total: number
    hydrated: boolean
    notice: CartNotice | null
    add: (item: CartItem, quantity?: number) => void
    setQuantity: (item: CartItem, quantity: number) => void
    remove: (slug: string) => void
    replace: (lines: Array<CartLine>) => void
    clear: () => void
    dismissNotice: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

const clampQuantity = (quantity: number): number => Math.min(ORDER_RULES.maxQuantityPerItem, Math.max(0, Math.round(quantity)))

const isLine = (value: unknown): value is CartLine => {
    if (!value || typeof value !== 'object') return false

    const line = value as Record<string, unknown>

    return typeof line.slug === 'string'
        && typeof line.name === 'string'
        && typeof line.price === 'number'
        && typeof line.quantity === 'number'
        && line.quantity > 0
}

const readStoredLines = (): Array<CartLine> => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)

        if (!raw) return []

        const parsed: unknown = JSON.parse(raw)

        if (!Array.isArray(parsed)) return []

        return parsed.filter(isLine).map(line => ({
            slug: line.slug,
            name: line.name,
            price: line.price,
            image: typeof line.image === 'string' ? line.image : null,
            quantity: clampQuantity(line.quantity)
        })).filter(line => line.quantity > 0)
    }
    catch {
        return []
    }
}

export const CartProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [lines, setLines] = useState<Array<CartLine>>([])
    const [hydrated, setHydrated] = useState(false)
    const [notice, setNotice] = useState<CartNotice | null>(null)

    useEffect(() => {
        setLines(readStoredLines())
        setHydrated(true)

        const onStorage = (event: StorageEvent): void => {
            if (event.key === STORAGE_KEY) setLines(readStoredLines())
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    useEffect(() => {
        if (!hydrated) return

        try {
            if (lines.length === 0) window.localStorage.removeItem(STORAGE_KEY)
            else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
        }
        catch {
            // Storage can be unavailable in private browsing. The order still works for this visit.
        }
    }, [lines, hydrated])

    const setQuantity = useCallback((item: CartItem, quantity: number): void => {
        const next = clampQuantity(quantity)

        setLines(current => {
            const exists = current.some(line => line.slug === item.slug)

            if (next === 0) return current.filter(line => line.slug !== item.slug)

            if (!exists) return [...current, { slug: item.slug, name: item.name, price: item.price, image: item.image, quantity: next }]

            return current.map(line => line.slug === item.slug ? { ...line, name: item.name, price: item.price, image: item.image, quantity: next } : line)
        })
    }, [])

    const add = useCallback((item: CartItem, quantity = 1): void => {
        setLines(current => {
            const existing = current.find(line => line.slug === item.slug)

            if (!existing) return [...current, { slug: item.slug, name: item.name, price: item.price, image: item.image, quantity: clampQuantity(quantity) }]

            return current.map(line => line.slug === item.slug ? { ...line, quantity: clampQuantity(line.quantity + quantity) } : line)
        })

        setNotice({ id: Date.now(), name: item.name, quantity })
    }, [])

    const remove = useCallback((slug: string): void => {
        setLines(current => current.filter(line => line.slug !== slug))
    }, [])

    const replace = useCallback((next: Array<CartLine>): void => setLines(next), [])

    const clear = useCallback((): void => setLines([]), [])

    const dismissNotice = useCallback((): void => setNotice(null), [])

    const value = useMemo<CartContextValue>(() => {
        const count = lines.reduce((sum, line) => sum + line.quantity, 0)
        const total = lines.reduce((sum, line) => sum + toCents(line.price) * line.quantity, 0) / 100

        return { lines, count, total, hydrated, notice, add, setQuantity, remove, replace, clear, dismissNotice }
    }, [lines, hydrated, notice, add, setQuantity, remove, replace, clear, dismissNotice])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider.')

    return context
}
